// ABOUTME: The right-hand table of the delete dialog, rendered with the design-system data table.
// ABOUTME: Fed by the delete queries so the rows shown are exactly the ones the action will touch.

import { Box, Typography } from '@ds/coolshop';
import { BaseTable } from '@ds/data-table';
import { SettingsContext } from '@src/modules/setting/SettingsContext';
import { getTablePaginationRowsLabel } from '@src/utils/form/shared';
import { useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';

export type DeleteTableRow = {
    id: string;
    code: string;
    listName?: string | null;
    parent?: string | null;
    /** Why this row is affected: the link field being emptied, or the cascade reason. */
    detail: string;
};

type LinkedNodesDeleteTableProps = {
    rows: DeleteTableRow[];
    totalCount: number;
    loading: boolean;
    /** Header of the last column, which differs between the unlink and the delete list. */
    detailHeader: string;
    page: number;
    pageSize: number;
    onPageChange: (page: number, pageSize: number) => void;
    emptyMessage: string;
};

export function LinkedNodesDeleteTable({
    rows,
    totalCount,
    loading,
    detailHeader,
    page,
    pageSize,
    onPageChange,
    emptyMessage,
}: LinkedNodesDeleteTableProps) {
    const { formatMessage } = useIntl();
    const { searchDataPagination } = useContext(SettingsContext);

    const columns = useMemo(
        () => [
            {
                id: 'code',
                accessorKey: 'code',
                // shared with every other table, so it is already translated
                header: formatMessage({ id: 'code', defaultMessage: 'Code' }),
            },
            {
                id: 'listName',
                accessorKey: 'listName',
                header: formatMessage({ id: 'node-delete.table.name', defaultMessage: 'Name' }),
            },
            { id: 'detail', accessorKey: 'detail', header: detailHeader },
            {
                id: 'parent',
                accessorKey: 'parent',
                header: formatMessage({ id: 'node-delete.table.parent', defaultMessage: 'Parent' }),
            },
        ],
        [formatMessage, detailHeader],
    );

    // The row count changes when the tab or the hierarchy does; without clamping, the table would
    // briefly be asked to show a page beyond the end of the new list.
    const lastPage = Math.max(0, Math.ceil(totalCount / pageSize) - 1);
    const safePage = Math.min(page, lastPage);

    if (!loading && rows.length === 0) {
        return (
            <Box p={3}>
                <Typography color="textSecondary">{emptyMessage}</Typography>
            </Box>
        );
    }

    return (
        // Server-side paging: the table is handed one page at a time, so it has to be told which
        // page that is — otherwise it renders the right rows under the wrong range label.
        <BaseTable
            data={rows}
            columns={columns}
            count={totalCount}
            isLoading={loading}
            selectedRows={[]}
            initialFilters={[]}
            disabledFilters={[]}
            manualPagination
            pageIndex={safePage}
            hasPreviousPage={safePage > 0}
            hasNextPage={(safePage + 1) * pageSize < totalCount}
            getTablePaginationRowsLabel={() =>
                getTablePaginationRowsLabel(pageSize, safePage, totalCount)
            }
            handleStateChange={state => {
                if (state.pagination) {
                    onPageChange(state.pagination.pageIndex, state.pagination.pageSize);
                }
            }}
            settings={{ pagination: searchDataPagination || undefined }}
            excludeDndProvider
        />
    );
}
